import React, { useState, useEffect } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate, useParams } from 'react-router-dom';
import { useStreamAuth } from '../hooks/useStreamAuth';
import { useWatchlist } from '../hooks/useWatchlist';
import { useContinueWatching } from '../hooks/useContinueWatching';
import { useMyReactions } from '../hooks/useMyReactions';
import { userDataService } from '../services/UserDataService';
import ChannelCard from '../components/ChannelCard';
import LoadingSpinner from '../components/auth/LoadingSpinner';
import Header from '../components/common/Header';
import ProgressBar from '../components/common/ProgressBar';

const MyListsPage = () => {
  const { loginWithRedirect } = useAuth0();
  const { user, isAuthenticated } = useStreamAuth();
  const navigate = useNavigate();
  const { tab } = useParams();
  
  const {
    watchlistData: watchlist,
    loading: watchlistLoading,
    error: watchlistError,
    isRemoving,
    removeFromWatchlist,
  } = useWatchlist();
  const { continueWatchingData: continueWatching, loading: continueWatchingLoading } =
    useContinueWatching();
  const { 
    reactionsData: myReactions, 
    loading: reactionsLoading, 
    error: reactionsError,
    removeReaction 
  } = useMyReactions();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Define available tabs with their configurations
  const tabs = [
    {
      id: 'watchlist',
      label: 'My List',
      icon: '❤️',
      count: watchlist.length,
      path: '/my-lists/watchlist'
    },
    {
      id: 'continue-watching',
      label: 'Continue Watching',
      icon: '▶️',
      count: continueWatching.length,
      path: '/my-lists/continue-watching'
    },
    {
      id: 'reactions',
      label: 'My Reactions',
      icon: '👍',
      count: myReactions.length,
      path: '/my-lists/reactions'
    }
  ];

  // Determine active tab from URL parameter, default to 'watchlist'
  const activeTab = tab && tabs.find(t => t.id === tab) ? tab : 'watchlist';

  // Redirect to default tab if no tab specified
  useEffect(() => {
    if (!tab) {
      navigate('/my-lists/watchlist', { replace: true });
    }
  }, [tab, navigate]);

  useEffect(() => {
    if (isAuthenticated && user) {
      setIsLoading(false);
    }
  }, [isAuthenticated, user]);

  // Combine loading states
  const isLoadingData = continueWatchingLoading || reactionsLoading || isLoading || (watchlistLoading && watchlist.length === 0);
  const hasError = watchlistError || reactionsError || error;

  const handleRemoveFromWatchlist = async channelId => {
    console.log('🗑️ MyListsPage: handleRemoveFromWatchlist called with channelId:', channelId);
    try {
      const result = await removeFromWatchlist(channelId);
      console.log('🗑️ MyListsPage: removeFromWatchlist result:', result);
    } catch (error) {
      console.error('MyListsPage: Error removing from watchlist:', error);
      setError(error.message);
    }
  };

  const handleRemoveReaction = async channelId => {
    console.log('🗑️ MyListsPage: handleRemoveReaction called with channelId:', channelId);
    try {
      const result = await removeReaction(channelId);
      console.log('🗑️ MyListsPage: removeReaction result:', result);
    } catch (error) {
      console.error('MyListsPage: Error removing reaction:', error);
      setError(error.message);
    }
  };

  const handleItemClick = async item => {
    try {
      const slug = item.parent_channel?.slug || item.slug;

      if (item.content_type === 'series' || item.childChannels?.length > 0) {
        const latestWatchedEpisode = userDataService.findLatestWatchedEpisode(item._id);
        if (latestWatchedEpisode) {
          navigate(`/watch/${slug}?${latestWatchedEpisode.episode._id}`);
        } else {
          navigate(`/watch/${slug}`);
        }
      } else {
        navigate(`/watch/${slug}`);
      }
    } catch (error) {
      console.error('MyListsPage: Error handling item click:', error);
      navigate(`/watch/${item.slug}`);
    }
  };

  const handleTabClick = (tabId) => {
    const selectedTab = tabs.find(t => t.id === tabId);
    if (selectedTab) {
      navigate(selectedTab.path);
    }
  };

  const renderWatchlistContent = () => {
    if (watchlist.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">❤️</div>
          <h3 className="text-xl font-semibold text-white mb-2">Your list is empty</h3>
          <p className="text-white/70 mb-6">
            Add movies and shows to your list by clicking the heart icon
          </p>
          <button
            className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
            onClick={() => navigate('/')}
          >
            Browse Content
          </button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {watchlist.map((item, index) => {
          console.log('🔍 MyListsPage: Rendering watchlist item:', {
            _id: item._id,
            title: item.title,
            dspro_id: item.dspro_id
          });
          
          return (
          <div key={item._id || index} className="relative">
            <ChannelCard
              channel={item}
              style="watchlist"
              index={index}
              externalClickHandle={() => handleItemClick(item)}
            />
            <button
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleRemoveFromWatchlist(item._id);
              }}
              disabled={isRemoving(item._id)}
              className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-700 transition-colors"
              title="Remove from list"
            >
              ×
            </button>
          </div>
          );
        })}
      </div>
    );
  };

  const renderContinueWatchingContent = () => {
    if (continueWatching.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">▶️</div>
          <h3 className="text-xl font-semibold text-white mb-2">Nothing to continue</h3>
          <p className="text-white/70 mb-6">
            Start watching something to see your progress here
          </p>
          <button
            className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
            onClick={() => navigate('/')}
          >
            Start Watching
          </button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {continueWatching.map((item, index) => (
          <div key={item._id || index} className="relative">
            <ChannelCard
              channel={item}
              style="continue-watching"
              index={index}
              externalClickHandle={() => handleItemClick(item)}
            />
            {item.progress && (
              <div className="absolute bottom-2 left-2 right-2">
                <ProgressBar 
                  progress={item.progress} 
                  className="h-1 bg-white/20"
                  fillClassName="bg-accent"
                />
              </div>
            )}
          </div>
        ))}
      </div>
    );
  };

  const renderReactionsContent = () => {
    console.log('🔍 MyListsPage: Rendering reactions content:', myReactions);
    
    if (myReactions.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="text-6xl mb-4">👍</div>
          <h3 className="text-xl font-semibold text-white mb-2">No reactions yet</h3>
          <p className="text-white/70 mb-6">
            Like or dislike content to see your reactions here
          </p>
          <button
            className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
            onClick={() => navigate('/')}
          >
            Explore Content
          </button>
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4">
        {myReactions.map((item, index) => {
          console.log('🔍 MyListsPage: Rendering reaction item:', {
            _id: item._id,
            dspro_id: item.dspro_id,
            user_reaction: item.user_reaction,
            reaction: item.reaction,
            title: item.title
          });
          
          return (
            <div key={item._id || index} className="relative">
              <ChannelCard
                channel={item}
                style="reactions"
                index={index}
                externalClickHandle={() => handleItemClick(item)}
              />
              <div className="absolute top-2 left-2 bg-black/70 rounded-full w-8 h-8 flex items-center justify-center">
                <span className="text-lg">
                  {item.user_reaction === 'like' ? '👍' : 
                   item.user_reaction === 'dislike' ? '👎' : 
                   item.user_reaction === 'double_like' ? '❤️' : 
                   item.user_reaction === 'double_dislike' ? '💔' : '?'}
                </span>
              </div>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  handleRemoveReaction(item.dspro_id || item._id);
                }}
                className="absolute top-2 right-2 bg-red-600 text-white rounded-full w-6 h-6 flex items-center justify-center text-xs hover:bg-red-700 transition-colors"
                title="Remove reaction"
              >
                ×
              </button>
            </div>
          );
        })}
      </div>
    );
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'watchlist':
        return renderWatchlistContent();
      case 'continue-watching':
        return renderContinueWatchingContent();
      case 'reactions':
        return renderReactionsContent();
      default:
        return renderWatchlistContent();
    }
  };

  const getPageTitle = () => {
    const currentTab = tabs.find(t => t.id === activeTab);
    return currentTab ? currentTab.label : 'My Lists';
  };

  // Authentication check
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-primary text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold mb-4">Sign in required</h2>
          <p className="text-white/70 mb-6">You need to sign in to view your lists</p>
          <button
            onClick={() => loginWithRedirect()}
            className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  // Loading state
  if (isLoadingData) {
    return (
      <div className="min-h-screen bg-primary">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex gap-8">
            {/* Sidebar skeleton */}
            <div className="w-64 bg-gray-900 rounded-lg p-6">
              <div className="space-y-4">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-12 bg-gray-800 rounded animate-pulse"></div>
                ))}
              </div>
            </div>
            {/* Content skeleton */}
            <div className="flex-1">
              <div className="flex justify-center items-center h-64">
                <LoadingSpinner />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Error state
  if (hasError) {
    return (
      <div className="min-h-screen bg-primary">
        <Header />
        <div className="container mx-auto px-4 py-8">
          <div className="flex gap-8">
            <div className="w-64 bg-gray-900 rounded-lg"></div>
            <div className="flex-1">
              <div className="text-center py-12">
                <div className="text-6xl mb-4">⚠️</div>
                <h3 className="text-xl font-semibold text-white mb-2">Something went wrong</h3>
                <p className="text-white/70 mb-6">{hasError}</p>
                <button
                  className="bg-accent text-white px-6 py-2 rounded-lg hover:bg-accent/80 transition-colors"
                  onClick={() => window.location.reload()}
                >
                  Try Again
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-primary text-white">
      <Header />
      
      {/* Use same container as homepage */}
      <div className="container mx-auto px-4 py-8">
        <div className="flex gap-8">
          {/* Vertical Sidebar Navigation */}
          <div className="w-64 bg-gray-900 rounded-lg">
            <div className="p-6">
              <h1 className="text-2xl font-bold text-white mb-8">My Lists</h1>
              
              <nav className="space-y-2">
                {tabs.map((tabConfig) => (
                  <button
                    key={tabConfig.id}
                    onClick={() => handleTabClick(tabConfig.id)}
                    className={`w-full flex items-center justify-between p-3 rounded-lg text-left transition-colors ${
                      activeTab === tabConfig.id
                        ? 'bg-accent text-white'
                        : 'text-white/70 hover:text-white hover:bg-gray-800'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <span className="text-lg">{tabConfig.icon}</span>
                      <span className="font-medium">{tabConfig.label}</span>
                    </div>
                    <span className={`text-sm px-2 py-1 rounded-full ${
                      activeTab === tabConfig.id
                        ? 'bg-white/20'
                        : 'bg-gray-700'
                    }`}>
                      {tabConfig.count}
                    </span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content Area */}
          <div className="flex-1">
            {/* Page Header */}
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-white mb-2">{getPageTitle()}</h2>
              <p className="text-white/70">
                {activeTab === 'watchlist' && 'Your favorite movies and shows'}
                {activeTab === 'continue-watching' && 'Pick up where you left off'}
                {activeTab === 'reactions' && 'Content you\'ve liked or disliked'}
              </p>
            </div>

            {/* Content */}
            {renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MyListsPage;